import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";
import { helpers } from "vuelidate/lib/validators";
import i18n from "@/lang/i18n";
import {
  LegalEntity,
  Oauth2TokenResponse,
  UserDef
} from "@/model/component.model";
import router from "@/router/router";
import { AlertBus } from "@/plugins/alerts/AlertBus";
import store from "@/vuex/store";
import { UserService } from "@/services/UserService";
import {
  ApiResponse,
  ChangePasswordTokenDTO,
  ConfigurationDTO,
  ForgotPasswordDTO,
  IApiKeyListBody,
  Oauth2TokenDef,
  OnlineMbeConfigurationDTO,
  ValidatePasswordTokenDTO
} from "@/model/data.model";
import { CustomerService } from "@/services/CustomerService";
import { GdprService } from "@/services/GdprService";
import { JwtHelper } from "@/utils/JwtHelper";
import createAuthRefreshInterceptor, {
  AxiosAuthRefreshRequestConfig
} from "axios-auth-refresh";
import { TaxDutyService } from "@/services/TaxDutyService";
import { _keycloak } from "@/bootstrap";
import { alertDangerReason } from "./Helpers";
import VueCookies from "vue-cookies";
import { getKeyValueConfig } from "@/utils/fetchKeyValueConfigLevel";
import WineService from "./WineService";

const DEFAULT_TOKEN_NAME = "mol-token";
const KEYCLOAK_TOKEN = "keycloak-token";
const DEFAULT_LEGAL_ENTITY_NAME = "mol-legal-entity";
export const axiosNotLoggedInstance = axios.create();

axiosNotLoggedInstance.interceptors.request.use(function(config: any) {
  config.headers["Source"] = "Online";
  config.headers["Authorization"] = "Basic " + btoa("teleport-fe:");
  config.headers["baseUrl"] = window.location.hostname;
  return config;
});

export const passwordRegex = helpers.regex(
  "password",
  /^[A-Za-z0-9 .,!?_-]{8,}$/
);

export class AuthService {
  userService: UserService = new UserService();

  login(username: string, password: string): AxiosPromise<AxiosResponse> {
    const seed = performance.now().toString();
    deleteToken();

    return this.getToken("password", username, password, "", seed)
      .then(res => {
        storeToken(res.data);
        return res;
      })
      .catch(err => {
        return Promise.reject(err.response.data);
      });
  }

  resetPassword(email: string) {
    const config: AxiosAuthRefreshRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/auth-registry/users/reset-password",
      data: {
        email: email,
        tenantId: getTenantId()
      },
      skipAuthRefresh: true
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  changePassword(
    username: string,
    currentPassword: string,
    newPassword: string
  ) {
    const config: AxiosAuthRefreshRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/auth-registry/users/set-password",
      data: {
        username: username,
        oldPassword: currentPassword,
        newPassword: newPassword
      },
      skipAuthRefresh: true
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  // added a logic to refresh the access token 1min before it expires, to avoid unnecessary server 401 responses
  // we kept the refresh on 401 response as backup, in case this one fails for some reason, so the user is not forced to login again
  silentRefreshToken() {
    axios.interceptors.response.use(
      (res: any) => {
        if (retrieveToken()) {
          if (
            new Date().getTime() >=
            retrieveToken().refresh_token_expire_timestamp
          ) {
            setTimeout(() => {
              AlertBus.$alerts.danger(i18n.t("expired_token")).then(() => {
                deleteToken();
              });
            }, 100);
            return Promise.reject(
              new Error("Refresh token expired, cannot reuse")
            );
          }
          // if the difference between current TS and expire TS is less or equal to 1 minute, refresh token
          if (
            retrieveToken().access_token_expire_timestamp -
              new Date().getTime() <=
            60000
          ) {
            // we use var authTokenRequest to queue token refresh api call, and avoid multiple token refresh caused
            // by multiple api calls, as well to wait for token refresh and avoid asynchronous api call use old token and generate 401
            _keycloak
              .updateToken(60)
              .then(() => {
                const data = buildOauth2TokenResponse(
                  _keycloak.token as string,
                  _keycloak.refreshToken as string
                );
                storeToken(data);
                store.dispatch("afterTokenStored");
                // once token is stored, we reset authTokenRequest var until the next token refresh
                return Promise.resolve(res);
              })
              .catch((err: any) => {
                console.error(err);
                setTimeout(() => {
                  AlertBus.$alerts.danger(i18n.t("expired_token")).then(() => {
                    deleteToken();
                  });
                }, 100);
                return Promise.reject(err);
              });
          }
          return Promise.resolve(res);
        }
        return Promise.resolve(res);
      },
      err => Promise.reject(err)
    );
  }

  async onBehalfLogin(username: any) {
    const params: { username: any } = {
      username: username
    };
    return axios
      .get(store.getters.KEYCLOAK_BASE_API_URL + "/mbe-api/oauth/on-behalf", {
        params: params
      })
      .then(res => {
        storeToken(res.data);
        return res;
      })
      .catch(err => {
        return Promise.reject(err.response.data);
      });
  }

  registerInterceptor() {
    this.addRefreshLogic();
    this.silentRefreshToken();

    //auto add API - MBE headers
    axios.interceptors.request.use(
      function(config: any) {
        const re = new RegExp(
          "oauth/token|oauth/login/google|reset-password|set-password"
        );

        const result = re.test(config.url);
        config.headers["Source"] = "Online";

        if (
          result &&
          !(config.url.indexOf("set-password") !== -1 && retrieveToken())
        ) {
          if (!config.headers.Authorization) {
            config.headers["Authorization"] = "Basic " + btoa("teleport-fe:");
          }
        } else {
          let tokenObj = retrieveToken();
          if (tokenObj === null) {
            tokenObj = retrieveKeycloakToken();
          }

          if (tokenObj === null) {
            config.headers["Authorization"] = "Basic " + btoa("teleport-fe:");
            setTimeout(() => {
              AlertBus.$alerts.danger(i18n.t("expired_token")).then(() => {
                deleteToken();
              });
            }, 100);
          } else {
            config.headers["Authorization"] = "Bearer " + tokenObj.access_token;
            const tenantid = getTenantId();
            config.headers["X-Teleport-Tenant"] = tenantid;
          }
        }

        config.headers["baseUrl"] = window.location.hostname;

        return config;
      },
      function(error: any) {
        // Do something with request error
        return Promise.reject(error);
      }
    );

    //network error interceptor
    axios.interceptors.response.use(undefined, (error: any) => {
      // If its a cancellation, just return it
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }
      return Promise.reject(error);
    });
  }

  addRefreshLogic() {
    // Function that will be called to refresh authorization
    if (retrieveToken()) {
      let isRefreshing = false;
      const refreshAuthLogic = (failedRequest: any) => {
        if (isRefreshing) {
          return Promise.reject(new Error("Refresh already in progress"));
        }
        if (!retrieveToken()) {
          return Promise.reject(new Error("Token is null during auth refresh"));
        }
        const currentTs = new Date().getTime();
        if (currentTs >= retrieveToken().refresh_token_expire_timestamp) {
          setTimeout(() => {
            AlertBus.$alerts.danger(i18n.t("expired_token")).then(() => {
              deleteToken();
            });
          }, 100);
          return Promise.reject(
            new Error("Refresh token expired, cannot reuse")
          );
        }
        isRefreshing = true;
        return _keycloak
          .updateToken(60)
          .then(() => {
            const data = buildOauth2TokenResponse(
              _keycloak.token as string,
              _keycloak.refreshToken as string
            );
            storeToken(data);
            store.dispatch("afterTokenStored");
            failedRequest.response.config.headers["Authorization"] =
              "Bearer " + retrieveToken().access_token;
            return Promise.resolve();
          })
          .catch(err => {
            // refresh, token not valid
            console.error(err);
            setTimeout(() => {
              AlertBus.$alerts.danger(i18n.t("expired_token")).then(() => {
                deleteToken();
              });
            }, 100);
            return Promise.reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      };
      // Instantiate the interceptor (you can chain it as it returns the axios instance)
      createAuthRefreshInterceptor(axios, refreshAuthLogic, {
        statusCodes: [401]
      });
    }
  }

  getToken(
    grantType: string,
    username: string,
    password: string,
    refreshToken: string,
    seed?: string,
    failedReq?: any
  ) {
    const body = new URLSearchParams();
    body.append("grant_type", grantType);
    if (grantType === "password") {
      body.append("username", username);
      body.append("password", password);
      body.append("seed", seed + "_" + store.getters.appVersion);
    }
    if (grantType === "refresh_token") {
      body.append("refresh_token", refreshToken);
    }
    if (failedReq) {
      console.error(failedReq.data); // log unauthorized reason
    }
    const options: any = {
      headers: {
        "cache-control": "no-cache",
        "Content-type": "application/x-www-form-urlencoded"
      },
      skipAuthRefresh: true
    };
    return axios.post(
      store.getters.KEYCLOAK_BASE_API_URL + "/protocol/openid-connect/token",
      body.toString(),
      options
    );
  }

  getApiKeyList(body: IApiKeyListBody): Promise<AxiosResponse<any[]>> {
    const config: AxiosAuthRefreshRequestConfig = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/auth-registry/apikey",
      params: {
        legalEntityType: body.legalEntityType,
        idRole: body.idRole,
        idEntity: body.idEntity,
        showInactive: body.showInactive,
        username: body.username,
        sort: body.sort,
        number: body.number,
        size: body.size
      }
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  postCreateApiKey(body: IApiKeyListBody): Promise<AxiosResponse<any[]>> {
    const config: AxiosAuthRefreshRequestConfig = {
      method: "POST",
      url: store.getters.BASE_API_URL + "/auth-registry/apikey",
      params: {
        legalEntityType: body.legalEntityType,
        idRole: body.idRole,
        idEntity: body.idEntity,
        showInactive: body.showInactive,
        username: body.username,
        sort: body.sort,
        number: body.number,
        size: body.size
      }
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  deleteApiKey(apikey: string): Promise<AxiosResponse<any[]>> {
    const config: AxiosAuthRefreshRequestConfig = {
      method: "DELETE",
      url: store.getters.BASE_API_URL + "/auth-registry/apikey",
      params: {
        apikey
      }
    };
    return axios.request(config).then(res => {
      return res.data;
    });
  }

  getLegalEntities(): Promise<AxiosResponse<LegalEntity[]>> {
    return axios
      .get(store.getters.KEYCLOAK_BASE_API_URL + "/mbe-api/oauth/mine")
      .then((res: AxiosResponse<LegalEntity[]>) => {
        store.commit("updateUserStores", res.data);
        return res;
      });
  }

  /* FUNCTION TO RETRIVE ALL INFORMATION ABOUT THE LOGGED USER */
  async selectStore(le: LegalEntity, no_department_text: string) {
    await axios
      .post(
        store.getters.KEYCLOAK_BASE_API_URL + "/mbe-api/oauth/select-store",
        le
      )
      .then(res => {
        updateInfoFromTokenResponse(res.data);
        updateInfoFromLegalEntity(le);
        storeToken(res.data);
        this.addRefreshLogic();
      });

    this.initApplicationContext(no_department_text);
  }

  async selectStoreHub(le: LegalEntity) {
    const options: any = {
      skipAuthRefresh: true
    };
    const url =
      store.getters.KEYCLOAK_BASE_API_URL + "/mbe-api/oauth/select-store";
    try {
      const response = await axios.post(url, le, options);
      const tokensResponse = {
        access: response.data.access_token as string,
        refresh: response.data.refresh_token as string
      };
      return tokensResponse;
    } catch (error) {
      alertDangerReason(error, { $alerts: AlertBus.$alerts });
      return null;
    }
  }

  async initApplicationContext(no_department_text: string) {
    await this.userService
      .getUserByUsername(store.getters["username"])
      .then((user: UserDef) => {
        store.commit("updateUser", user);
      });

    const info = await CustomerService.loadCustomerInfo(store);
    const config = await CustomerService.loadCustomerConfig(store).then(
      (configuration: OnlineMbeConfigurationDTO) => {
        if (store.getters.language !== configuration.language) {
          i18n.i18next.changeLanguage(configuration.language);
          store.commit("updateLanguage", configuration.language);
        }
        store.commit("updateQaplaConfig", {
          qaplaEnabled: configuration.enableQapla,
          qaplaLevel: configuration.qaplaLevel
        });
      }
    );

    const departments = CustomerService.loadCustomerDepartments(
      store,
      no_department_text
    );
    const store_addresses = await CustomerService.loadCustomerStoreAddresses(
      store
    );
    const store_info = await CustomerService.loadCustomerStoreInfo(store);
    const extra_service_config = CustomerService.loadCustomerExtraServiceConfig(
      store
    );
    const courier_visibility_config = CustomerService.loadCustomerCourierVisibilityConfig(
      store
    );
    const thermic_config = CustomerService.loadCustomerThermicConfig(store);
    const tax_duty_config = TaxDutyService.prototype.initTaxDutyUserConfiguration();
    const wine_visibility = await getWineVisibilty().then(res => {
      store.commit(
        "updateWineVisibilty",
        getKeyValueConfig(res, "SYS_WINE_MOL_ENABLED")
      );
      store.commit(
        "updatePrintOnlineEnabled",
        getKeyValueConfig(res, "ENABLE_PRINT_ONLINE")
      );
      const printOnlineUrl = res.find(r => r.confKey === "PRINT_ONLINE_URL");
      if (printOnlineUrl)
        store.commit("updatePrintOnlineStoreUrl", printOnlineUrl.confValue);
    });
    const claim_config = CustomerService.loadClaimConfig(store);
    await Promise.all([
      info,
      config,
      departments,
      store_addresses,
      store_info,
      extra_service_config,
      courier_visibility_config,
      thermic_config,
      tax_duty_config,
      wine_visibility,
      claim_config
    ]);
  }

  revoke(): AxiosPromise<void> {
    const options: any = {
      skipAuthRefresh: true
    };
    return axios
      .delete(
        store.getters.KEYCLOAK_BASE_API_URL + "/mbe-api/oauth/revoke",
        options
      )
      .then(res => {
        deleteToken();
        deleteKcToken();
        return res;
      })
      .catch(res => {
        deleteToken();
        deleteKcToken();
        return res;
      });
  }

  forgotPassword(request: ForgotPasswordDTO): Promise<ApiResponse> {
    const options = {
      skipAuthRefresh: true
    };
    return axiosNotLoggedInstance
      .post(
        store.getters.BASE_API_URL + "/auth-registry/users/password/forgot",
        request,
        options as any
      )
      .then(res => {
        return res.data as ApiResponse;
      });
  }

  validatePasswordToken(token: string): Promise<ApiResponse> {
    const options = {
      skipAuthRefresh: true
    };
    return axiosNotLoggedInstance
      .post(
        store.getters.BASE_API_URL +
          "/auth-registry/users/password/validate-reset-token",
        { token: token } as ValidatePasswordTokenDTO,
        options as any
      )
      .then(res => {
        return res.data as ApiResponse;
      });
  }

  changePasswordToken(request: ChangePasswordTokenDTO): Promise<ApiResponse> {
    const options = {
      skipAuthRefresh: true
    };
    return axiosNotLoggedInstance
      .post(
        store.getters.BASE_API_URL + "/auth-registry/users/password/change",
        request,
        options as any
      )
      .then(res => {
        return res.data as ApiResponse;
      });
  }
}

///////////
export function tokenValid(userInfo: any): boolean {
  let result = false;

  //If no user info passed as parameter retrieve from local storage
  if (userInfo === null) {
    userInfo = retrieveToken();
  }

  if (userInfo !== null) {
    if (
      !JwtHelper.isTokenExpired(userInfo.refresh_token) ||
      !JwtHelper.isTokenExpired(userInfo.access_token)
    ) {
      result = true;
      /**
       * Check if the user passed the second authentication step.
       */
      if (userInfo["legal-entity-id"] === undefined) {
        result = false;
      }
    }
  }
  return result;
}
/**
 * Token storage informations
 */

export function retrieveToken(): any {
  const retrievedStr = localStorage.getItem(DEFAULT_TOKEN_NAME);
  if (retrievedStr === null) {
    return null;
  }
  const parsedToken = JSON.parse(retrievedStr) as Oauth2TokenDef;
  if (!parsedToken.access_token || !parsedToken.refresh_token) {
    return null;
  }
  return parsedToken;
}

const pathsVerifiedByUuid = ["/register/customers/"];

export function checkIfPathIsVerifiedByUuid(fullPath: string, id: string) {
  const pathsVerifiedByUuidWithParameter = pathsVerifiedByUuid.map(
    path => path + id
  );
  return pathsVerifiedByUuidWithParameter.includes(fullPath);
}

export async function verifyUuid(uuid: string) {
  return await GdprService.prototype.verifyUuidValidity(uuid);
}

export function storeToken(token: Oauth2TokenResponse) {
  const refreshExpire = JwtHelper.getTokenExpirationDate(token.refresh_token);
  const accessExpire = JwtHelper.getTokenExpirationDate(token.access_token);
  const extendedToken = {
    ...token,
    timestamp: new Date().getTime(),
    access_token_expire_timestamp: accessExpire ? accessExpire.getTime() : 0,
    refresh_token_expire_timestamp: refreshExpire ? refreshExpire.getTime() : 0
  } as Oauth2TokenDef;
  localStorage.setItem(DEFAULT_TOKEN_NAME, JSON.stringify(extendedToken));
  store.dispatch("afterTokenStored");
}

export function storeKeycloakToken(token: Oauth2TokenResponse) {
  localStorage.setItem(KEYCLOAK_TOKEN, JSON.stringify(token));
  return token;
}

export function buildOauth2TokenResponse(token: string, refresh_token: string) {
  const refreshExpire = JwtHelper.getTokenExpirationDate(refresh_token);
  const accessExpire = JwtHelper.getTokenExpirationDate(token);
  const tokenData = JwtHelper.decodeToken(token);
  return {
    ...tokenData,
    access_token: token,
    refresh_token: refresh_token,
    timestamp: new Date().getTime(),
    access_token_expire_timestamp: accessExpire ? accessExpire.getTime() : 0,
    refresh_token_expire_timestamp: refreshExpire ? refreshExpire.getTime() : 0
  } as Oauth2TokenDef;
}

export function retrieveKeycloakToken(): any {
  const retrievedStr = localStorage.getItem(KEYCLOAK_TOKEN);
  if (retrievedStr === null) {
    return null;
  }
  const parsedToken = JSON.parse(retrievedStr) as Oauth2TokenDef;
  if (!parsedToken.access_token || !parsedToken.refresh_token) {
    return null;
  }
  return parsedToken;
}

export function storeLegalEntity(legalEntity: LegalEntity) {
  localStorage.setItem(DEFAULT_LEGAL_ENTITY_NAME, JSON.stringify(legalEntity));
}

export function retrieveLegalEntity(): any {
  const retrievedStr = localStorage.getItem(DEFAULT_LEGAL_ENTITY_NAME);
  if (retrievedStr === null) {
    return null;
  }
  const retrieved = JSON.parse(retrievedStr) as LegalEntity;
  return retrieved;
}

export function deleteToken(redirectTo: string = null) {
  localStorage.removeItem(DEFAULT_TOKEN_NAME);
  clearKeycloakCookies();
  return deleteKcToken(redirectTo);
}

export function deleteKcToken(redirectTo: string = null) {
  localStorage.removeItem(KEYCLOAK_TOKEN);
  redirectTo = redirectTo !== null ? redirectTo : "/login";
  return _keycloak.logout({
    redirectUri: `${window.location.origin}${redirectTo}`
  });
}

export function clearKeycloakCookies() {
  const cookiesToDelete = [
    "AUTH_SESSION_ID",
    "AUTH_SESSION_ID_LEGACY",
    "KC_RESTART",
    "KEYCLOAK_SESSION",
    "KEYCLOAK_SESSION_LEGACY",
    "KEYCLOAK_IDENTITY",
    "KEYCLOAK_IDENTITY_LEGACY",
    "KEYCLOAK_OTP_DATA_IDENTIFIER",
    "KEYCLOAK_OTP_DATA_IDENTIFIER_LEGACY",
    "KEYCLOAK_LOCALE"
  ];
  cookiesToDelete.forEach(cookie => {
    const $cookies = VueCookies as any;
    $cookies.remove(cookie, "/");
    $cookies.remove(cookie);
  });
}

export function getStoreId() {
  const token = retrieveToken();
  if (!token) return null;
  return store.getters["customer/storeInfo"].store.id;
}

export function getTenantId() {
  const token = retrieveToken();
  if (!token) return null;
  return token.tenant;
}

export function getMultiStoreId() {
  return store.getters["customer/storeInfo"].store.multiStoreId;
}

export function getCurrencies() {
  return axios
    .get(store.getters.BASE_API_URL + "/tenants/currencies", {
      params: {
        countryId: store.getters["customer/storeAddresses"][0].countryId
      }
    })
    .then((res: any) => {
      return res.data;
    });
}

export function updateInfoFromTokenResponse(response: any) {
  store.commit("updateUserEmail", response.email);
  store.commit("updateUsername", response.username);
  store.commit(
    "customer/setCustomerMbeId",
    response["customer-mbe-identifier"]
  );
}

export function updateInfoFromLegalEntity(le: LegalEntity) {
  store.commit("updateStoreSelected", le);
  storeLegalEntity(le);
}

export function getEventsTable(page: number, size: number, filters: any) {
  if (filters.events == "") {
    filters.events = [];
  }
  const config: any = {
    method: "POST",
    url: store.getters.BASE_API_URL + "/auth-registry/keycloak/events",
    data: {
      events: filters.eventsRawValue,
      fromData: filters.fromData,
      toData: filters.toData,
      username: filters.username
    },
    params: {
      page: page,
      size: size
    }
  };
  return axios
    .request(config)
    .then(res => {
      return res.data;
    })
    .catch(err => {
      console.error("Response failed with message: " + err.message);
      return Promise.reject(err);
    });
}

export function getEventsTypes() {
  const config: any = {
    method: "GET",
    url: store.getters.BASE_API_URL + "/auth-registry/keycloak/events/types"
  };
  return axios
    .request(config)
    .then(res => {
      return res.data;
    })
    .catch(err => {
      console.error("Response failed with message: " + err.message);
      return Promise.reject(err);
    });
}

export function getDeviceManagementTable(
  page: number,
  size: number,
  filters: any
) {
  const config: any = {
    method: "POST",
    url: store.getters.BASE_API_URL + "/auth-registry/device/management",
    params: {
      page: page,
      size: size
    },
    data: {
      username: filters.username,
      approvedBy: filters.approvedBy,
      fromData: filters.fromData,
      toData: filters.toData
    }
  };
  return axios
    .request(config)
    .then(res => {
      return res.data;
    })
    .catch(err => {
      console.error("Response failed with message: " + err.message);
      return Promise.reject(err);
    });
}

export function revokeDevice(id: string) {
  const config: any = {
    method: "POST",
    url:
      store.getters.BASE_API_URL + "/auth-registry/device/management/untrust",
    params: {
      id: id
    }
  };
  return axios
    .request(config)
    .then(res => {
      return res.data;
    })
    .catch(err => {
      console.error("Response failed with message: " + err.message);
      return Promise.reject(err);
    });
}

export function getWineVisibilty() {
  const objArray = [
    {
      tenantId: getTenantId(),
      multistoreId: null,
      storeId: null,
      confKey: "SYS_WINE_MOL_ENABLED"
    },
    {
      tenantId: getTenantId(),
      multistoreId: getMultiStoreId(),
      storeId: getStoreId(),
      confKey: "ENABLE_PRINT_ONLINE"
    },
    {
      tenantId: getTenantId(),
      multistoreId: getMultiStoreId(),
      storeId: getStoreId(),
      confKey: "PRINT_ONLINE_URL"
    }
  ] as ConfigurationDTO[];

  const config: AxiosRequestConfig = {
    method: "POST",
    data: objArray,
    url: store.getters.BASE_API_URL + "/stores/kvstore/configurations"
  };
  return axios
    .request(config)
    .then((res: AxiosResponse<ConfigurationDTO[]>) => {
      return res.data as ConfigurationDTO[];
    });
}
